import { Dialog } from "@kobalte/core/dialog";
import { UpdateProjectRequestConfig, useStorytellClient } from "@storytell/client";
import type { ProjectId } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, createEffect, createMemo, createSignal, Show } from "solid-js";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

export const EditProjectModal: Component<EditProjectModalProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();
  const { t, currentLanguage } = useI18n();

  // Ensure reactivity by tracking language changes
  createMemo(() => currentLanguage());

  const project = () => wire.projects?.get?.(props.projectId as ProjectId);

  const [editName, setEditName] = createSignal("");
  const [editDescription, setEditDescription] = createSignal("");
  const [saveError, setSaveError] = createSignal<string | null>(null);
  const [isSaving, setIsSaving] = createSignal(false);

  let nameInputRef: HTMLInputElement | undefined;

  // Initialize form when modal opens
  createEffect(() => {
    if (props.isOpen) {
      const proj = project();
      if (proj) {
        setEditName(proj.name);
        setEditDescription(proj.description || "");
        setSaveError(null);
        setTimeout(() => {
          nameInputRef?.focus();
          nameInputRef?.select();
        }, 100);
      }
    }
  });

  const handleSave = async () => {
    const proj = project();
    if (!proj) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const request = UpdateProjectRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(
        request,
        { id: props.projectId },
        {
          name: editName(),
          description: editDescription(),
        }
      );

      if (response.isErr()) {
        setSaveError(t("project_update_failed"));
        return;
      }
      trackFeature({ feature: "project", action: "rename", via: "button" });

      wire.projects?.set?.(props.projectId as ProjectId, { ...response.value, grants: project()?.grants });
      props.onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && editName().trim()) {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center p-4">
              <Dialog.Content class="relative w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4 p-1">
                  <TbX class="size-5" />
                </Dialog.CloseButton>

                <Dialog.Title class="mb-6 text-lg font-semibold">{t("project_edit_modal_title")}</Dialog.Title>

                <div class="space-y-4">
                  <div>
                    <label class="mb-1.5 block text-sm font-medium text-secondary-text">
                      {t("label.project_name")}
                    </label>
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={editName()}
                      onInput={(e) => setEditName(e.currentTarget.value)}
                      onKeyDown={handleKeyDown}
                      class="w-full rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
                      placeholder={t("label.project_name")}
                    />
                  </div>

                  <div>
                    <label class="mb-1.5 block text-sm font-medium text-secondary-text">
                      {t("project_description_placeholder")}
                    </label>
                    <textarea
                      value={editDescription()}
                      onInput={(e) => setEditDescription(e.currentTarget.value)}
                      class="w-full resize-none rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
                      placeholder={t("project_description_placeholder")}
                      rows={3}
                    />
                  </div>

                  <Show when={saveError()}>
                    <div class="rounded-lg border border-red-500/20 bg-red-500/10 p-3">
                      <p class="text-sm text-red-500">{saveError()}</p>
                    </div>
                  </Show>
                </div>

                <div class="mt-6 flex items-center justify-end gap-3">
                  <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                    {t("common.cancel")}
                  </StButton>
                  <StButton
                    onClick={() => void handleSave()}
                    disabled={!editName().trim() || isSaving()}
                    class="bg-brand px-4 py-2 text-on-brand hover:bg-brand-hover disabled:opacity-50"
                  >
                    <Show when={isSaving()} fallback={t("common.save")}>
                      {t("common.saving")}
                    </Show>
                  </StButton>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
